import { useMediaQuery } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreSalesPerformanceResponse, useStoreInformation } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { commonTranslations, storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { ToggleChipGroup } from '../../../common/toggleChipGroup/ToggleChipGroup';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { NoDataForPeriodView } from '../noDataForPeriodView/NoDataForPeriodView';
import {
  chipDataMapping,
  ChipKey,
  ChipOption,
  getIndexStatus,
  getSalesByGarmentGroupData,
  SalesAggregatedRow,
  useChipOptionsMap,
} from '../sales.util';
import { SalesSummaryCard, SalesSummarySlider } from '../salesSummaryCard/SalesSummaryCard';

type SalesByGarmentGroupWidgetProps = {
  isLoading: boolean;
  isError: boolean;
  salesPerformanceData: StoreSalesPerformanceResponse | null;
  isExpanded: boolean;
  expandButton: React.ReactNode;
  selectedFilter: string | undefined;
  retry: () => void;
};

enum SalesByGarmentGroupColumnType {
  METRIC = 'metric',
  SALESNETTHISYEAR = 'salesnetTY',
  DIFFERENCELASTYEAR = 'diffLY',
  SOB = 'sob',
  DRIVINGUNIT = 'du',
  INDEXLASTYEAR = 'indexLY',
  LASTYEARVSAREA = 'lyVsArea',
  LASTYEARVSSALESMARKET = 'lyVsSalesMarket',
}

export const SalesByGarmentGroupWidget: FC<SalesByGarmentGroupWidgetProps> = ({
  isLoading,
  isError,
  salesPerformanceData,
  isExpanded,
  expandButton,
  selectedFilter,
  retry,
}) => {
  const { t } = useTranslation();
  const { chipOptionsMap } = useChipOptionsMap();
  const {
    salesByGarmentGroup: salesByGarmentGroupTranslations,
    salesByIndex: salesByIndexTranslations,
    headerTooltips: headerTooltipsTranslations,
  } = storeOverviewTranslations;

  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;

  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [noDataView, setNoDataView] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<SalesAggregatedRow | null>(null);
  const [selectedChip, setSelectedChip] = useState<ChipKey>('LCD');
  const [totalRow, setTotalRow] = useState<SalesAggregatedRow>({
    metric: '',
    salesNetThisYear: 0,
    differenceLastYear: 0,
    sob: 0,
    drivingUnit: 0,
    indexLastYear: 0,
    lastYearVsArea: 0,
    lastYearVsSalesMarket: 0,
  });

  const salesByGarmentGroupData = useMemo(
    () => (salesPerformanceData ? getSalesByGarmentGroupData(salesPerformanceData, selectedFilter) : null),
    [salesPerformanceData, selectedFilter],
  );

  const tableData = useMemo(() => {
    if (!salesByGarmentGroupData) return [];
    const selectedIndexData = salesByGarmentGroupData[chipDataMapping[selectedChip]] || {};
    const { total, ...rest } = selectedIndexData;
    setTotalRow(total);
    const rowLength = Object.values(rest).length;
    setNoDataView(rowLength === 0);
    return Object.values(rest);
  }, [salesByGarmentGroupData, selectedChip]);

  const headerItems: HeaderRowItem<SalesByGarmentGroupColumnType>[] = [
    {
      id: SalesByGarmentGroupColumnType.METRIC,
      width: '20%',
      content: (
        <Typography type="body3">
          {selectedFilter
            ? t(
                storeOverviewTranslations.departmentLabel.key,
                storeOverviewTranslations.departmentLabel.defaultValue,
              )
            : t(
                storeOverviewTranslations.garmentGroupLabel.key,
                storeOverviewTranslations.garmentGroupLabel.defaultValue,
              )}
        </Typography>
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.SALESNETTHISYEAR,
      width: isHMBrand ? '15%' : '20%',
      content: (
        <HeaderTooltip
          headerText={t(
            storeOverviewTranslations.salesNetThisYear.key,
            storeOverviewTranslations.salesNetThisYear.defaultValue,
          )}
          tooltipText={t(
            headerTooltipsTranslations.salesNetTY.key,
            headerTooltipsTranslations.salesNetTY.defaultValue,
          )}
        />
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.DIFFERENCELASTYEAR,
      width: isHMBrand ? '15%' : '20%',
      content: (
        <HeaderTooltip
          headerText="Diff LY"
          tooltipText={t(headerTooltipsTranslations.diffMny.key, headerTooltipsTranslations.diffMny.defaultValue)}
        />
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.SOB,
      width: '10%',
      content: (
        <HeaderTooltip
          headerText="SOB"
          tooltipText={t(
            headerTooltipsTranslations.sobPercent.key,
            headerTooltipsTranslations.sobPercent.defaultValue,
          )}
        />
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.DRIVINGUNIT,
      width: '10%',
      content: (
        <HeaderTooltip
          headerText="DU"
          tooltipText={t(
            headerTooltipsTranslations.drivingUnit.key,
            headerTooltipsTranslations.drivingUnit.defaultValue,
          )}
        />
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.INDEXLASTYEAR,
      width: '10%',
      content: (
        <HeaderTooltip
          headerText="vs LY"
          tooltipText={t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)}
        />
      ),
    },
    {
      id: SalesByGarmentGroupColumnType.LASTYEARVSAREA,
      width: '10%',
      content: (
        <HeaderTooltip
          headerText="vs Area"
          tooltipText={t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue)}
        />
      ),
    },
    ...(isHMBrand
      ? [
          {
            id: SalesByGarmentGroupColumnType.LASTYEARVSSALESMARKET,
            width: '10%',
            content: (
              <HeaderTooltip
                headerText="vs SM"
                tooltipText={t(headerTooltipsTranslations.vsSM.key, headerTooltipsTranslations.vsSM.defaultValue)}
              />
            ),
          },
        ]
      : []),
  ];

  const chipOptions: ChipOption[] = [
    chipOptionsMap.LCD,
    chipOptionsMap.CW,
    chipOptionsMap.LCW,
    chipOptionsMap.L4Wks,
    chipOptionsMap.AccTY,
  ];

  const getTableRow = (item: SalesAggregatedRow) => {
    const {
      salesNetThisYear,
      differenceLastYear,
      sob,
      drivingUnit,
      indexLastYear,
      lastYearVsArea,
      lastYearVsSalesMarket,
    } = item;

    return [
      {
        id: SalesByGarmentGroupColumnType.SALESNETTHISYEAR,
        content: <Typography>{formatAmountWithSeparator(Math.round(salesNetThisYear))}</Typography>,
      },
      {
        id: SalesByGarmentGroupColumnType.DIFFERENCELASTYEAR,
        content: <Typography>{formatAmountWithSeparator(Math.round(differenceLastYear))}</Typography>,
      },
      {
        id: SalesByGarmentGroupColumnType.SOB,
        content: <Typography>{formatAmountWithSeparator(sob, '%', 1)}</Typography>,
      },
      {
        id: SalesByGarmentGroupColumnType.DRIVINGUNIT,
        content: <Typography>{formatAmountWithSeparator(drivingUnit, '%', 1, false)}</Typography>,
      },
      {
        id: SalesByGarmentGroupColumnType.INDEXLASTYEAR,
        content: (
          <StyledIndexTypography status={getIndexStatus(indexLastYear)}>{indexLastYear}</StyledIndexTypography>
        ),
      },
      {
        id: SalesByGarmentGroupColumnType.LASTYEARVSAREA,
        content: (
          <StyledIndexTypography status={getIndexStatus(lastYearVsArea)}>{lastYearVsArea}</StyledIndexTypography>
        ),
      },
      ...(isHMBrand
        ? [
            {
              id: SalesByGarmentGroupColumnType.LASTYEARVSSALESMARKET,
              content: (
                <StyledIndexTypography status={getIndexStatus(lastYearVsSalesMarket)}>
                  {lastYearVsSalesMarket}
                </StyledIndexTypography>
              ),
            },
          ]
        : []),
    ];
  };

  const handleCardDetails = (cardDetails: SalesAggregatedRow) => {
    setIsSliderOpen(true);
    setSelectedRowData(cardDetails);
  };

  return (
    <ContainerDrawer
      isOpen={isSliderOpen}
      onClose={() => setIsSliderOpen(false)}
      borderRadius={[2]}
      sliderContent={
        <SalesSummarySlider
          data={selectedRowData}
          subText={
            selectedFilter
              ? t(
                  salesByGarmentGroupTranslations.sliderTitle2.key,
                  salesByGarmentGroupTranslations.sliderTitle2.defaultValue,
                )
              : t(
                  salesByGarmentGroupTranslations.sliderTitle1.key,
                  salesByGarmentGroupTranslations.sliderTitle1.defaultValue,
                )
          }
        />
      }
      width={'300px'}
    >
      <StoreWidgetContent
        widgetVariant="tall"
        title={
          selectedFilter
            ? t(salesByGarmentGroupTranslations.title2.key, salesByGarmentGroupTranslations.title2.defaultValue)
            : t(salesByGarmentGroupTranslations.title1.key, salesByGarmentGroupTranslations.title1.defaultValue)
        }
        tooltipText={t(salesByIndexTranslations.tooltip.key, salesByIndexTranslations.tooltip.defaultValue)}
        isLoading={isLoading}
        isError={isError}
        isNoData={!salesByGarmentGroupData}
        isExpanded={isExpanded}
        errorRetryHandler={retry}
        rightContainerElements={isSmallDesktop ? null : expandButton}
        errorText={t(
          salesByGarmentGroupTranslations.errorText.key,
          salesByGarmentGroupTranslations.errorText.defaultValue,
        )}
        widgetContent={
          <ColumnFlexContainer height={'100%'}>
            <ToggleChipGroup
              chipOptions={chipOptions}
              selectedChip={selectedChip}
              setSelectedChip={(chip: ChipKey) => {
                setSelectedChip(chip);
              }}
            />
            {noDataView && <NoDataForPeriodView />}
            {!isMobile && !noDataView && salesByGarmentGroupData && (
              <ColumnFlexContainer padding={[0, 0, 2]} flex={1} data-testid="sales-by-garment-group-table-wrapper">
                <Table
                  data-testid="sales-by-garment-group-table"
                  items={tableData}
                  headerItems={headerItems}
                  stickyHeader
                  stickyFirstColumn
                  fixedHeight={isSmallDesktop ? '260px' : isExpanded ? '600px' : '400px'}
                  sxProps={{ padding: '8px' }}
                  headerSxProps={{ padding: '8px' }}
                  footerSxProps={{ padding: '16px 8px' }}
                  firstCellMinWidth="100px"
                  getRowItems={(item) => {
                    const row = getTableRow(item);
                    return [
                      {
                        id: SalesByGarmentGroupColumnType.METRIC,
                        content: <Typography type="body3">{item.metric}</Typography>,
                      },
                      ...row,
                    ];
                  }}
                  footerItems={[
                    {
                      id: SalesByGarmentGroupColumnType.METRIC,
                      content: (
                        <Typography type="body3">
                          {t(commonTranslations.total.key, commonTranslations.total.defaultValue)}
                        </Typography>
                      ),
                    },
                    ...getTableRow(totalRow),
                  ]}
                />
              </ColumnFlexContainer>
            )}
            {isMobile && !noDataView && salesByGarmentGroupData && (
              <ColumnFlexContainer data-testid="sales-by-garment-group-list" flex={1}>
                <ColumnFlexContainer gap={[2]} padding={[0, 0, 2]} overflow="auto" height={[54]}>
                  {tableData.map((item) => (
                    <SalesSummaryCard key={item.metric} data={item} isTotal={false} onClick={handleCardDetails} />
                  ))}
                </ColumnFlexContainer>
                <SalesSummaryCard data={totalRow} isTotal onClick={handleCardDetails} />
              </ColumnFlexContainer>
            )}
          </ColumnFlexContainer>
        }
      />
    </ContainerDrawer>
  );
};
